import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, PenLine, Camera, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useLanguage } from '@/i18n/LanguageContext';
import { PetPeek } from '@/components/PetPeek';
import { PetBackdrop } from '@/components/PetBackdrop';
import { ASSETS } from '@/assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange }) => (
  <div className="flex gap-0.5 text-brand-orange">
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        disabled={!onChange}
        onClick={() => onChange && onChange(i)}
        data-testid={`rating-star-${i}`}
        className={onChange ? 'hover:scale-125 transition-transform' : 'cursor-default'}
      >
        <Star className="w-4 h-4" fill={i <= value ? 'currentColor' : 'none'} />
      </button>
    ))}
  </div>
);

export const Reviews = () => {
  const { t } = useLanguage();
  const [realReviews, setRealReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [rating, setRating] = useState(5);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orderRef] = useState(() => new URLSearchParams(window.location.search).get('ref') || '');

  useEffect(() => {
    axios.get(`${API}/reviews`).then(({ data }) => setRealReviews(data)).catch(() => {});
  }, []);

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error(t.reviews.form.photoHint);
      return;
    }
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('text', text);
      fd.append('rating', String(rating));
      if (orderRef) fd.append('order_ref', orderRef);
      if (photo) fd.append('photo', photo);
      await axios.post(`${API}/reviews`, fd);
      toast.success(t.reviews.form.thanks);
      setOpen(false);
      setName('');
      setText('');
      setRating(5);
      setPhoto(null);
      setPreview(null);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t.reviews.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="reviews" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <PetBackdrop src={ASSETS.catDark} className="right-[-100px] top-[-40px] w-[580px] h-[580px] hidden lg:block" opacity={0.1} />
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 relative z-10">

        <div className="lg:col-span-4 lg:sticky lg:top-28 self-start space-y-6 relative">
          <PetPeek type="cat" className="-top-8 right-0 hidden lg:block" />
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-brand-orange">{t.reviews.overline}</p>
          <div className="font-display text-8xl md:text-9xl leading-[0.8] text-brand-orange" data-testid="reviews-big-number">
            {t.reviews.bigNumber}
          </div>
          <p className="text-base md:text-lg font-medium text-brand-green/80 max-w-xs">{t.reviews.bigLabel}</p>
          <p className="text-[11px] font-bold uppercase tracking-wider text-brand-green/50 max-w-xs">{t.reviews.note}</p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="review-share-card"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-brand-green text-brand-green text-xs font-extrabold uppercase tracking-wider hover:bg-brand-green hover:text-brand-cream transition-colors"
          >
            <PenLine className="w-4 h-4" /> {t.reviews.share.cta}
          </button>
        </div>

        <div className="lg:col-span-8 columns-1 sm:columns-2 gap-5 [&>*]:mb-5 [&>*]:break-inside-avoid">
          {realReviews.length === 0 && t.reviews.items.map((review) => (
            <div key={review.name} className="p-7 rounded-3xl bg-brand-bone/85 border-2 border-brand-green/10">
              <div className="flex items-center justify-between mb-3">
                <Stars value={5} />
                <span className="text-[10px] font-bold uppercase tracking-wider text-brand-green/50 border border-dashed border-brand-green/30 rounded-full px-2 py-0.5">{t.reviews.sample}</span>
              </div>
              <p className="text-sm font-medium italic text-brand-green/90 leading-relaxed">“{review.text}”</p>
              <div className="mt-5 pt-3 border-t-2 border-dashed border-brand-green/15 text-xs font-extrabold text-brand-green">
                {review.name}
              </div>
            </div>
          ))}

          {realReviews.map((review) => (
            <div key={review.id} data-testid={`review-card-${review.id}`} className="rounded-3xl bg-brand-bone/85 border-2 border-brand-green/10 overflow-hidden">
              {review.photo_url && (
                <img src={review.photo_url} alt={`${review.name} pet`} className="w-full aspect-[4/3] object-cover" loading="lazy" />
              )}
              <div className="p-7">
                <Stars value={review.rating} />
                <p className="text-sm font-medium italic text-brand-green/90 leading-relaxed mt-3">“{review.text}”</p>
                <div className="mt-5 pt-3 border-t-2 border-dashed border-brand-green/15 text-xs font-extrabold text-brand-green">
                  {review.name}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-cream border-2 border-brand-green/20 rounded-3xl max-w-md" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase text-brand-green">{t.reviews.share.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
            <div>
              <label htmlFor="review-name" className="block text-[11px] font-extrabold uppercase tracking-[0.15em] text-brand-green mb-1.5">{t.reviews.form.nameLabel}</label>
              <input
                id="review-name"
                data-testid="review-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-brand-green/20 bg-white text-sm text-brand-green focus:outline-none focus:border-brand-orange transition-colors"
              />
            </div>
            <div>
              <label htmlFor="review-text" className="block text-[11px] font-extrabold uppercase tracking-[0.15em] text-brand-green mb-1.5">{t.reviews.form.textLabel}</label>
              <textarea
                id="review-text"
                data-testid="review-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                required
                minLength={5}
                maxLength={600}
                rows={4}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-brand-green/20 bg-white text-sm text-brand-green focus:outline-none focus:border-brand-orange transition-colors resize-none"
              />
            </div>
            <div>
              <span className="block text-[11px] font-extrabold uppercase tracking-[0.15em] text-brand-green mb-1.5">{t.reviews.form.ratingLabel}</span>
              <Stars value={rating} onChange={setRating} />
            </div>
            <div>
              <label htmlFor="review-photo" className="block text-[11px] font-extrabold uppercase tracking-[0.15em] text-brand-green mb-1.5">{t.reviews.form.photoLabel}</label>
              <label
                htmlFor="review-photo"
                data-testid="review-photo-drop"
                className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed border-brand-green/25 bg-white cursor-pointer hover:border-brand-orange transition-colors"
              >
                {preview ? (
                  <img src={preview} alt="preview" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <Camera className="w-5 h-5 text-brand-orange" />
                )}
                <span className="text-xs font-medium text-brand-green/70">{t.reviews.form.photoHint}</span>
              </label>
              <input id="review-photo" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhoto} data-testid="review-photo-input" />
            </div>
            <button
              type="submit"
              data-testid="review-submit-btn"
              disabled={submitting}
              className="w-full py-3.5 rounded-full bg-brand-orange text-white font-extrabold uppercase tracking-wider hover:bg-[#C75007] hover:scale-[0.98] transition-all disabled:opacity-70 inline-flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {t.reviews.form.submit}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
